// Studio de restitution.
//
// Deux temps : la LISTE des livrables du projet, et l'ÉDITEUR d'un livrable
// (composition à gauche, aperçu vivant à droite). L'aperçu répond au besoin
// exprimé : « avoir un aperçu lors de sa création ». Il lit les données réelles,
// donc ce qu'on voit est ce que le commanditaire verra.
import { useState } from 'react';
import {
  useCreateDeliverable,
  useDeleteDeliverable,
  useDeliverables,
  useUpdateDeliverable,
} from '../api/queries';
import type { Deliverable, SectionKey } from '../api/types';
import { Button, Chip, EmptyState, ErrorBanner, Eyebrow, Spinner } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';
import { SECTION_CATALOG, sectionLabel } from './sections';
import { Report } from './Report';
import './studio.css';

// --- Éditeur ---------------------------------------------------------------

function Editor({
  projectId,
  projectName,
  deliverable,
  onClose,
}: {
  projectId: string;
  projectName: string;
  /** Absent = création ; présent = édition d'un livrable existant. */
  deliverable: Deliverable | null;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(deliverable?.title ?? '');
  const [sections, setSections] = useState<SectionKey[]>(
    deliverable?.sections ?? ['synthese', 'adoption', 'insights'],
  );
  const create = useCreateDeliverable(projectId);
  const update = useUpdateDeliverable(projectId);
  const busy = create.isPending || update.isPending;

  const toggle = (key: SectionKey) =>
    setSections((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  async function save() {
    if (!title.trim()) return;
    try {
      if (deliverable) {
        await update.mutateAsync({ id: deliverable.id, patch: { title, sections } });
      } else {
        await create.mutateAsync({ title, sections });
      }
      onClose();
    } catch {
      /* rendu par le bandeau ci-dessous */
    }
  }

  return (
    <div className="studio-editor">
      <div className="studio-compose">
        <div className="zy-page-head__texte">
          <Eyebrow>{deliverable ? 'Modifier la restitution' : 'Nouvelle restitution'}</Eyebrow>
          <h1 className="t-h2">Composer le livrable</h1>
        </div>

        <label className="zy-field">
          <span className="zy-field__label">Titre de la restitution</span>
          <input
            className="zy-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Restitution commanditaire — T2 2026"
          />
        </label>

        <fieldset className="section-picker">
          <legend className="zy-field__label">Sections à inclure</legend>
          {SECTION_CATALOG.map((s) => {
            const on = sections.includes(s.key);
            return (
              <label key={s.key} className={`section-toggle${on ? ' section-toggle-on' : ''}`}>
                <input className="zy-check" type="checkbox" checked={on} onChange={() => toggle(s.key)} />
                <span>
                  <span className="section-toggle-label">{s.label}</span>
                  <span className="section-toggle-hint">{s.hint}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        {create.error || update.error ? (
          <ErrorBanner>
            {(create.error ?? update.error) instanceof Error
              ? (create.error ?? update.error)!.message
              : 'Enregistrement impossible.'}
          </ErrorBanner>
        ) : null}

        <div className="studio-actions">
          <Button variant="ghost" onClick={onClose}>
            Retour
          </Button>
          <Button variant="accent" onClick={save} loading={busy} disabled={!title.trim()}>
            {deliverable ? 'Enregistrer' : 'Créer la restitution'}
          </Button>
          <Button variant="ghost" onClick={() => window.print()}>
            Imprimer / PDF
          </Button>
        </div>
      </div>

      {/* Aperçu vivant : ce que verra le commanditaire, avec les données du jour. */}
      <div className="studio-preview" aria-label="Aperçu du livrable">
        <Report projectId={projectId} projectName={projectName} title={title} sections={sections} />
      </div>
    </div>
  );
}

// --- Liste -----------------------------------------------------------------

function DeliverableCard({
  projectId,
  deliverable,
  canWrite,
  onOpen,
}: {
  projectId: string;
  deliverable: Deliverable;
  canWrite: boolean;
  onOpen: () => void;
}) {
  const del = useDeleteDeliverable(projectId);
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="card deliverable">
      <div className="card-head">
        <div>
          <Eyebrow>Restitution</Eyebrow>
          <h2 className="t-h3">{deliverable.title}</h2>
        </div>
        <Chip>
          {deliverable.sections.length} section{deliverable.sections.length > 1 ? 's' : ''}
        </Chip>
      </div>
      {deliverable.sections.length > 0 ? (
        <p className="deliverable-sections">
          {deliverable.sections.map((s) => sectionLabel(s)).join(' · ')}
        </p>
      ) : null}
      <div className="deliverable-foot">
        <Button variant="ghost" onClick={onOpen}>
          {canWrite ? 'Ouvrir' : 'Consulter'}
        </Button>
        {canWrite ? (
          confirming ? (
            <span className="deliverable-confirm">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Annuler
              </Button>
              <Button variant="danger" onClick={() => del.mutate(deliverable.id)} loading={del.isPending}>
                Supprimer
              </Button>
            </span>
          ) : (
            <Button variant="link-danger" size="s" className="u-push-end" type="button" onClick={() => setConfirming(true)}>
              Supprimer
            </Button>
          )
        ) : null}
      </div>
    </li>
  );
}

export function StudioScreen({ projectId, projectName }: { projectId: string; projectName: string }) {
  const query = useDeliverables(projectId);
  const canWrite = useCanWrite(projectId);
  // `undefined` = liste ; `null` = éditeur en création ; objet = édition.
  const [editing, setEditing] = useState<Deliverable | null | undefined>(undefined);

  if (editing !== undefined) {
    return (
      <Editor
        projectId={projectId}
        projectName={projectName}
        deliverable={editing}
        onClose={() => setEditing(undefined)}
      />
    );
  }

  return (
    <main className="screen">
      <div className="zy-page-head__texte">
        <Eyebrow>Studio de restitution</Eyebrow>
        <h1 className="t-h1">Livrables</h1>
        <p className="zy-intro">
          Assemblez un rapport pour le commanditaire à partir des données du projet. Le rapport est
          dynamique : il reflète toujours l’état courant, et s’exporte en PDF par impression.
        </p>
        {canWrite ? (
          <div className="screen-actions">
            <Button variant="accent" onClick={() => setEditing(null)}>
              Nouvelle restitution
            </Button>
          </div>
        ) : null}
      </div>

      {query.isPending ? (
        <div className="screen-loading">
          <Spinner label="Chargement des livrables…" />
        </div>
      ) : query.isError ? (
        <>
          <ErrorBanner>
            {query.error instanceof Error ? query.error.message : 'Chargement impossible.'}
          </ErrorBanner>
          <Button variant="ghost" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        </>
      ) : query.data.length === 0 ? (
        <EmptyState
          title="Aucune restitution pour l’instant"
          description="Un livrable rassemble la synthèse, l’Adoption Score, les insights et les personas en un rapport prêt à présenter."
        />
      ) : (
        <ul className="card-list">
          {query.data.map((d) => (
            <DeliverableCard
              key={d.id}
              projectId={projectId}
              deliverable={d}
              canWrite={canWrite}
              onOpen={() => setEditing(d)}
            />
          ))}
        </ul>
      )}
    </main>
  );
}
